/**
 * Right-hand review panel for the Medical Necessity screen.
 *
 * Drives guideline/criteria pagination over the loaded
 * {@link LoadedNccnEvaluation}, renders the current criterion for review, and
 * persists reviewer feedback via {@link useNccnReview}.
 * @module NccnEvaluationPanel
 */
import React, { useEffect, useRef, useState } from "react";
import { LoaderMessage } from "../../../components/loaderMessage/loaderMessage";
import { LoadedNccnEvaluation } from "../../../data-model/nccnEvaluation";
import ChevronRight from "../../../svg/chevron-right";
import { MedicalNecessityAnalytics } from "../analytics/useMedicalNecessityAnalytics";
import { useNccnPanelActions } from "../hooks/useNccnPanelActions";
import { useNccnPanelViewTracking } from "../hooks/useNccnPanelViewTracking";

import { useNccnReview } from "../hooks/useNccnReview";
import {
  computeMandatoryReviewProgress,
  getGuidelineDisplayPositions,
} from "../utils/nccnReviewFeedback";
import { GuidelineHeaderRow } from "./GuidelineHeaderRow";
import {
  GuidelineSummaryHeader,
  GuidelineSummaryList,
} from "./GuidelineSummaryList";
import {
  NCCN_PANEL_FOOTER_BACK_CLASS,
  NCCN_PANEL_FOOTER_PRIMARY_CLASS,
  NCCN_PANEL_HEADER_BAR_CLASS,
  NCCN_PANEL_ROOT_CLASS,
} from "./nccnEvaluationPanelStyles";
import { NccnStepthroughBody } from "./NccnStepthroughBody";
import { SkipToNextGuidelineModal } from "./SkipToNextGuidelineModal";

/** Props for {@link NccnEvaluationPanel}. */
interface NccnEvaluationPanelProps {
  evaluation: LoadedNccnEvaluation | null;
  isLoading: boolean;
  orderId: string;
  patientId?: string;
  orgId?: string;
  analytics: MedicalNecessityAnalytics;
}

type ReviewPhase = "stepthrough" | "summary";

/**
 * Renders the NCCN evaluation review panel with loading/empty handling.
 *
 * @param props - Loaded evaluation, loading flag, order/patient/org IDs, analytics
 * @returns The rendered panel
 */
export const NccnEvaluationPanel: React.FC<NccnEvaluationPanelProps> = ({
  evaluation,
  isLoading,
  orderId,
  patientId,
  orgId,
  analytics,
}) => {
  const review = useNccnReview({ evaluation, orderId, patientId, orgId });
  const {
    guidelines,
    guidelineIndex,
    guidelineCount,
    criteriaIndex,
    criteriaCount,
    currentGuideline,
    currentItem,
    isReviewComplete,
    canSubmitReview,
    orderResult,
    aiEvaluation,
    hasNextMandatoryGuideline,
    nextSkipTargetIndex,
    mandatoryTrackPosition,
    mandatoryTrackTotal,
    isSubmitting,
    isOverrideMode,
    beginOverride,
    endOverride,
    goStep,
    isFirstStep,
    isLastStep,
    isSummaryStep,
    markCorrect,
    saveFeedback,
    currentReview,
    updateGuidelineReview,
    skipToNextGuideline,
    commitDraft,
    submitReview,
    refetch,
  } = review;

  const [phase, setPhase] = useState<ReviewPhase>("stepthrough");
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const pendingActionIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!evaluation) return;
    const progress = computeMandatoryReviewProgress(evaluation.payload);
    setPhase(
      progress.total === 0 || evaluation.review_submitted || progress.isComplete
        ? "summary"
        : "stepthrough",
    );
  }, [evaluation]);

  const guidelineDisplayPosition =
    getGuidelineDisplayPositions(guidelines)[guidelineIndex] ??
    guidelineIndex + 1;

  // The header's NN/NN badge counts the reviewer's progress through the
  // guidelines being stepped through (the mandatory track), which advances
  // monotonically with each Next/Skip. In override mode the current guideline
  // is off the mandatory track, so the badge falls back to the grouped display
  // position to match the summary row the guideline was opened from. The
  // "N Total Guidelines" tag always shows the full order count.
  const headerPosition = isOverrideMode
    ? guidelineDisplayPosition
    : mandatoryTrackPosition;
  const headerTotal = isOverrideMode ? guidelineCount : mandatoryTrackTotal;

  useNccnPanelViewTracking({
    analytics,
    orderId,
    isLoading,
    hasEvaluation: !!evaluation,
    phase,
    currentGuideline,
    currentItem,
    guidelineIndex,
    criteriaIndex,
    guidelineDisplayPosition,
    isSummaryStep,
    guidelines,
    aiOrderVerdict: aiEvaluation,
    humanOrderVerdict: orderResult,
    pendingActionIdRef,
  });

  const {
    handleReview,
    handleOverrideToMet,
    handleEditReview,
    handleOverrideDone,
    handleSkipToNext,
    navigate,
  } = useNccnPanelActions({
    analytics,
    guidelines,
    currentGuideline,
    currentItem,
    guidelineDisplayPosition,
    criteriaIndex,
    isSummaryStep,
    currentReview,
    aiOrderVerdict: aiEvaluation,
    humanOrderVerdict: orderResult,
    isOverrideMode,
    hasNextMandatoryGuideline,
    nextSkipTargetIndex,
    pendingActionIdRef,
    beginOverride,
    endOverride,
    goStep,
    skipToNextGuideline,
    commitDraft,
    submitReview,
    refetch,
    setPhase,
  });

  const isSummaryReasoningMissing =
    isSummaryStep && currentReview.verdictReasoning.trim().length === 0;
  const isNextDisabled = isSummaryStep
    ? !currentReview.verdict || isSummaryReasoningMissing
    : !!currentItem && !currentItem.ai_answer_feedback;

  // "Review" replaces "Next" / "Next Guideline" on the last step or as soon as
  // the early-submit condition holds (one Human Met, or all mandatory
  // guidelines Human Not Met), so the reviewer can reach the review screen
  // without finishing every guideline.
  const showReviewButton = isOverrideMode
    ? isLastStep
    : isLastStep || canSubmitReview;

  if (isLoading) {
    return (
      <div className={NCCN_PANEL_ROOT_CLASS}>
        <LoaderMessage message="Loading evaluation..." />
      </div>
    );
  }

  if (phase === "summary") {
    return (
      <div className={NCCN_PANEL_ROOT_CLASS}>
        <div className={NCCN_PANEL_HEADER_BAR_CLASS}>
          <GuidelineSummaryHeader guidelines={guidelines} />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-[1.25rem] overflow-auto px-[1.25rem] py-[1.125rem]">
          {guidelines.length === 0 ? (
            <p className="text-sm text-primaryGray-10">
              No NCCN guidelines associated with this order.
            </p>
          ) : (
            <GuidelineSummaryList
              guidelines={guidelines}
              onOverrideToMet={handleOverrideToMet}
              onEditReview={handleEditReview}
              onCopy={(guideline, displayIndex) =>
                analytics.trackGuidelineCopyClicked(guideline, displayIndex)
              }
              showHeader={false}
            />
          )}
        </div>
      </div>
    );
  }

  if (!currentGuideline) {
    return (
      <div className={`${NCCN_PANEL_ROOT_CLASS} items-center justify-center`}>
        <p className="text-sm text-primaryGray-10">
          No evaluation available for this order.
        </p>
      </div>
    );
  }

  return (
    <div className={NCCN_PANEL_ROOT_CLASS}>
      <div className={NCCN_PANEL_HEADER_BAR_CLASS}>
        <GuidelineHeaderRow
          position={headerPosition}
          total={headerTotal}
          orderTotal={guidelineCount}
          result={currentGuideline.metadata?.evaluation?.final_result}
        />
      </div>

      <NccnStepthroughBody
        guideline={currentGuideline}
        guidelineIndex={guidelineIndex}
        guidelineDisplayPosition={guidelineDisplayPosition}
        criteriaIndex={criteriaIndex}
        criteriaCount={criteriaCount}
        currentItem={currentItem}
        isSummaryStep={isSummaryStep}
        isSubmitting={isSubmitting}
        currentReview={currentReview}
        updateGuidelineReview={updateGuidelineReview}
        markCorrect={markCorrect}
        saveFeedback={saveFeedback}
        analytics={analytics}
      />

      <div className="flex items-center justify-between self-stretch border-t border-[#E6E6E6] bg-white p-4">
        {!isSummaryStep ? (
          <button
            type="button"
            onClick={() => setIsSkipModalOpen(true)}
            disabled={isSubmitting}
            className="flex h-8 min-w-0 cursor-pointer items-center gap-1.5 rounded-[0.25rem] border border-[#D6D6D6] bg-white px-3.5 text-[0.75rem] font-semibold leading-[1.125rem] text-[#0F0F0F] [font-family:Lato] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="truncate">Skip to next guideline</span>
            <ChevronRight
              stroke="#0F0F0F"
              width={13}
              height={13}
              className="shrink-0"
            />
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
        <div className="flex items-center gap-[0.625rem]">
          <button
            type="button"
            onClick={() => void navigate(-1)}
            disabled={isFirstStep || isSubmitting}
            className={NCCN_PANEL_FOOTER_BACK_CLASS}
          >
            Back
          </button>
          {showReviewButton ? (
            <button
              type="button"
              onClick={() =>
                void (isOverrideMode ? handleOverrideDone() : handleReview())
              }
              disabled={
                isOverrideMode
                  ? isNextDisabled || isSubmitting
                  : (isLastStep && !isReviewComplete && !canSubmitReview) ||
                    (isSummaryStep && !currentReview.verdict) ||
                    isSummaryReasoningMissing ||
                    isSubmitting
              }
              className={NCCN_PANEL_FOOTER_PRIMARY_CLASS}
            >
              {isOverrideMode ? "Done" : "Review"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void navigate(1)}
              disabled={isNextDisabled || isSubmitting}
              className={NCCN_PANEL_FOOTER_PRIMARY_CLASS}
            >
              {isSummaryStep ? "Next Guideline" : "Next"}
            </button>
          )}
        </div>
      </div>

      {isSkipModalOpen && (
        <SkipToNextGuidelineModal
          isSaving={isSubmitting}
          onCancel={() => {
            if (currentGuideline) {
              analytics.trackSkipCancelClicked(
                currentGuideline,
                guidelineDisplayPosition,
              );
            }
            setIsSkipModalOpen(false);
          }}
          onSkip={(reasoning) =>
            void (async () => {
              const saved = await handleSkipToNext(reasoning);
              if (saved) setIsSkipModalOpen(false);
            })()
          }
        />
      )}
    </div>
  );
};
